import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { ProjectFilterProvider } from "@/lib/ProjectFilterContext";
import { ThemeProvider } from "@/lib/ThemeContext";
import { FocusProvider } from "@/lib/FocusContext";
import SidebarLayout from "@/components/SidebarLayout";
import Login from "@/pages/Login";
import Projection from "@/pages/Projection";
import SalesBookings from "@/pages/SalesBookings";
import Milestones from "@/pages/Milestones";
import LandSales from "@/pages/LandSales";
import Collections from "@/pages/Collections";
import ImportHistory from "@/pages/ImportHistory";
import Planning from "@/pages/Planning";
import Upload from "@/pages/Upload";
import Settings from "@/pages/Settings";
import Actuals from "@/pages/Actuals";
import Analytics from "@/pages/Analytics";
import SalesIntelligence from "@/pages/SalesIntelligence";
import CashflowOverview from "@/pages/CashflowOverview";
import DevelopmentCost from "@/pages/outflows/DevelopmentCost";
import MarketingExpenses from "@/pages/outflows/MarketingExpenses";
import ConstructionEPC from "@/pages/outflows/ConstructionEPC";
import SalariesHR from "@/pages/outflows/SalariesHR";
import StatutoryCorpus from "@/pages/outflows/StatutoryCorpus";
import CommittedLands from "@/pages/outflows/CommittedLands";
import LoanFacilities from "@/pages/loans/LoanFacilities";
import DebtSchedule from "@/pages/loans/DebtSchedule";
import UserRoles from "@/pages/UserRoles";
import OtherReceipts from "@/pages/OtherReceipts";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-dark text-sm">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <ProtectedRoute>
            <SidebarLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<CashflowOverview />} />
        <Route path="/projection" element={<Projection />} />
        <Route path="/cashflow" element={<Navigate to="/" replace />} />
        <Route path="/outflows/development" element={<DevelopmentCost />} />
        <Route path="/outflows/marketing" element={<MarketingExpenses />} />
        <Route path="/outflows/epc" element={<ConstructionEPC />} />
        <Route path="/outflows/hr" element={<SalariesHR />} />
        <Route path="/outflows/statutory" element={<StatutoryCorpus />} />
        <Route path="/outflows/committed-lands" element={<CommittedLands />} />
        <Route path="/loans/facilities" element={<LoanFacilities />} />
        <Route path="/loans/schedule" element={<DebtSchedule />} />
        <Route path="/users" element={<UserRoles />} />
        <Route path="/other-receipts" element={<OtherReceipts />} />
        <Route path="/timeline" element={<Navigate to="/" replace />} />
        <Route path="/reconciliation" element={<Navigate to="/" replace />} />
        <Route path="/flags" element={<Navigate to="/" replace />} />
        <Route path="/sales" element={<SalesBookings />} />
        <Route path="/milestones" element={<Milestones />} />
        <Route path="/land-sales" element={<LandSales />} />
        <Route path="/collections" element={<Collections />} />
        <Route path="/planning" element={<Planning />} />
        <Route path="/actuals" element={<Actuals />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/sales-intelligence" element={<SalesIntelligence />} />
        {/* Old planning URLs → unified page */}
        <Route path="/what-if" element={<Navigate to="/planning" replace />} />
        <Route path="/scenarios" element={<Navigate to="/planning" replace />} />
        <Route path="/treasury" element={<Navigate to="/planning" replace />} />
        <Route path="/import" element={<Upload />} />
        <Route path="/import-history" element={<ImportHistory />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ProjectFilterProvider>
            <FocusProvider>
              <AppRoutes />
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "var(--cf-surface-glass-strong)",
                  backdropFilter: "blur(20px) saturate(160%)",
                  WebkitBackdropFilter: "blur(20px) saturate(160%)",
                  border: "1px solid var(--cf-hairline-strong)",
                  color: "var(--cf-ink)",
                  boxShadow: "var(--cf-shadow-md)",
                  borderRadius: "12px",
                  fontFamily: "Manrope, sans-serif",
                },
              }}
            />
            </FocusProvider>
          </ProjectFilterProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
